import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { formatEcoNumber, formatTimestamp } from "@/lib/documents";
import { fetchRelatedChangeOrders } from "@/pages/documents/engineeringChangeOrderApi";

export function RelatedEngineeringChangesPanel(props: {
  documentId?: string;
  sku?: string;
  productCollectionId?: string;
  detailPathPrefix: string;
}): JSX.Element | null {
  const enabled =
    Boolean(props.documentId?.trim()) ||
    Boolean(props.sku?.trim()) ||
    Boolean(props.productCollectionId?.trim());

  const relatedQuery = useQuery({
    queryKey: [
      "document-change-orders-related",
      props.documentId,
      props.sku,
      props.productCollectionId,
    ],
    queryFn: () =>
      fetchRelatedChangeOrders({
        documentId: props.documentId,
        sku: props.sku,
        productCollectionId: props.productCollectionId,
      }),
    enabled,
  });

  if (!enabled) return null;
  if (relatedQuery.isLoading) {
    return <p className="text-sm text-slate-500">Loading related engineering changes…</p>;
  }
  if (relatedQuery.isError) {
    return (
      <p className="text-sm text-red-600">{(relatedQuery.error as Error).message}</p>
    );
  }

  const rows = relatedQuery.data?.changeOrders ?? [];
  if (rows.length === 0) return null;

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold text-slate-900">Related engineering changes</h3>
      <ul className="space-y-2 text-sm">
        {rows.map((row) => (
          <li key={row.id} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <Link
              className="font-medium text-brand-700 hover:underline"
              to={`${props.detailPathPrefix}/${row.id}`}
            >
              {formatEcoNumber(row.number)} — {row.title}
            </Link>
            <div className="text-xs text-slate-600">
              {row.status} · updated {formatTimestamp(row.updated_at)}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
